import { Users } from "@phosphor-icons/react";
import { GlobalToken, Select, SelectProps } from "antd";
import { useTranslate } from "@refinedev/core";
import { useAntTheme } from "hooks/useAntTheme";
import { useWorkspace } from "hooks/useWorkspace";
import { useSession } from "hooks/useSession";
import { selectUserCan } from "helpers/utils";
import { ALL_WORKSPACES_ID } from "@/constants";
import { Workspace } from "types";
import styled from "styled-components";
import { useEffect } from "react";

export const WorkspaceSwitch = () => {
  const { theme } = useAntTheme();
  const t = useTranslate();
  const { accesses } = useSession();

  const { workspaces, workspaceId, changeWorkspace, isAllWorkspacesSelected } =
    useWorkspace();

  const canSeeAll = !!selectUserCan(accesses, {
    subject: "Media.Project",
    action: "read",
    scope: "ORG",
  });

  // If "All" was persisted but user lost permission, reset to first workspace
  useEffect(() => {
    if (isAllWorkspacesSelected && !canSeeAll && workspaces.length > 0) {
      changeWorkspace({ workspace: workspaces[0] });
    }
  }, [isAllWorkspacesSelected, canSeeAll, workspaces, changeWorkspace]);

  if (!workspaces || workspaces.length < 1) {
    return null;
  }

  const options = [
    ...(canSeeAll
      ? [
          {
            value: ALL_WORKSPACES_ID,
            label: t("media.workspaces.index.allWorkspaces"),
          },
        ]
      : []),
    ...workspaces.map(({ id, name }) => ({ value: id, label: name })),
  ];

  const isDisabled = !canSeeAll && workspaces.length < 2;

  return (
    <StyledSelect
      data-testid="WorkspaceSwitch"
      showSearch
      optionFilterProp="label"
      theme={theme}
      size="large"
      disabled={isDisabled}
      onChange={(value) => {
        if (value === ALL_WORKSPACES_ID) {
          changeWorkspace({
            workspace: {
              id: ALL_WORKSPACES_ID,
              name: t("media.workspaces.index.allWorkspaces"),
            } as Workspace,
          });
        } else {
          changeWorkspace({
            workspace: workspaces.find((x) => x.id === value),
          });
        }
      }}
      value={isAllWorkspacesSelected ? ALL_WORKSPACES_ID : workspaceId}
      options={options}
      style={{ width: "100%" }}
      virtual={false} // disable for automated pass to find test org
      suffixIcon={<Users size={20} weight="bold" />}
    />
  );
};

const StyledSelect = styled(Select)<SelectProps & { theme: GlobalToken }>`
  .ant-select-selector {
    border-width: 2px !important;
    border-color: ${({ theme }) => theme.colorBorderSecondary} !important;
    box-shadow: none !important;
  }
  &:not(.ant-select-focused) {
    .ant-select-selector {
      color: ${({ theme }) => theme.colorPrimary} !important;
    }
  }
`;
